import {
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  inject,
  isDevMode,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { StoreModule } from '@ngrx/store';
import { FormsModule } from '@angular/forms';
import { EffectsModule } from '@ngrx/effects';
import { RouterModule } from '@angular/router';
import localeFr from '@angular/common/locales/fr';
import localeHu from '@angular/common/locales/hu';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule } from '@angular/platform-browser';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ServiceWorkerModule } from '@angular/service-worker';

import { provideTranslateService, TranslatePipe } from '@ngx-translate/core';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { NgxStripeModule } from 'ngx-stripe';

import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';

import { environment } from '@env/environment';

import { AppComponent } from '@app/app.component';
import { APP_EFFECTS, APP_STORE } from '@app/app.store';

import { CoreModule } from '@core/core.module';
import { SharedModule } from '@shared/shared.module';
import { AppRoutingModule } from '@app/app-routing.module';

import { ANALYTICS_WINDOW, AnalyticWindow } from '@shared/interfaces/window.interface';

import { NotificationService } from '@core/services/notification/notification.service';
import { MarketplaceOrderingUnavailableModalService } from '@core/services/marketplace-ordering-unavailable-modal/marketplace-ordering-unavailable-modal.service';

import { loadMarketplaceLocale } from '@app/load-marketplace-locale.initializer';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ensureMarketplaceIsLoaded } from '@app/ensure-marketplace-is-loaded.initializer';
import { activateCb2PreviewMode } from '@app/activate-cb2-preview-mode.initializer';
import { ReceiveReceiptModalComponent } from '@shared/modals/receive-receipt-modal/receive-receipt-modal.component';
import { ConsoleInterceptorService } from '@core/services/app/console-interceptor.service';
import { TrackEventErrorHandlerService } from '@core/services/app/track-event-error-handler.service';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { doodApiInterceptor } from '@core/interceptors/dood-api/dood-api.interceptor';

registerLocaleData(localeFr);
registerLocaleData(localeHu, 'hu-HU');

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent, ReceiveReceiptModalComponent],
  imports: [
    // Ngrx
    StoreModule.forRoot(APP_STORE),
    EffectsModule.forRoot(APP_EFFECTS),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      serialize: true,
      name: 'Dood -- NgRX',
      logOnly: !environment.production,
      connectInZone: true,
    }),
    // Basics
    FormsModule,
    RouterModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    // Dood
    CoreModule,
    SharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.withServiceWorkers,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Google Maps
    GoogleMapsModule,
    // Stripe.js is lazy-loaded via StripeLoaderService when a Stripe payment is needed
    NgxStripeModule.forRoot(),
    TranslatePipe,
  ],
  providers: [
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: APP_BASE_HREF, useValue: environment.baseUrl },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: `.json?cb=${new Date().getTime()}`,
      }),
    }),
    // Ionic
    InAppBrowser,
    // Services
    NotificationService,
    // Initializers
    provideAppInitializer(() => {
      if (!isDevMode()) {
        return;
      }
      inject(ConsoleInterceptorService).initialize();
    }),
    // CB2 preview mode must be activated (and the boot listener armed) BEFORE
    // `ensureMarketplaceIsLoaded` runs, so `loadMarketplace$` can skip the
    // domain API and await the dashboard stub in preview mode.
    provideAppInitializer(() => {
      const initializerFn = activateCb2PreviewMode();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = ensureMarketplaceIsLoaded();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      inject(MarketplaceOrderingUnavailableModalService).start();
    }),
    // Misc
    {
      provide: LOCALE_ID,
      deps: [MarketplaceStoreSelector],
      useFactory: loadMarketplaceLocale,
    },
    {
      provide: ANALYTICS_WINDOW,
      useFactory: (): AnalyticWindow => {
        return window as unknown as AnalyticWindow;
      },
    },
    { provide: ErrorHandler, useClass: TrackEventErrorHandlerService },
    // {
    //   provide: TENANT_ID,
    //   deps: [MarketplaceService],
    //   useFactory: (marketplaceService: MarketplaceService): string | null | undefined => {
    //     return marketplaceService.getFirebaseTenantId();
    //   },
    // },

    provideHttpClient(withInterceptors([doodApiInterceptor])),
  ],
})
export class AppModule {}
